import { createSelector } from 'reselect'
import { currentPathSelector } from 'selectors/app'
import { selectCurrentPage } from 'selectors/location'
import {
  SETTINGS_WIDGET_CONTACT_PAGE,
  SETTINGS_WIDGET_KB_PAGE,
  SETTINGS_WIDGET_CUSTOMIZE_PAGE,
  SETTINGS_WIDGET_TRANSLATE_PAGE,
  SETTINGS_WIDGET_INSTALL_PAGE,
  SETTINGS_WIDGET_FIRST_INSTALL_PAGE,
  SETTINGS_WIDGET_CHAT_PAGE,
  SETTINGS_WIDGET_CHAT_CONVERSATION_PAGE,
  SETTINGS_WIDGET_CHAT_ROUTING_PAGE,
  SETTINGS_WIDGET_CHAT_ACL_PAGE,
  SETTINGS_WIDGET_CHAT_BOT_OVERVIEW_PAGE,
  SETTINGS_WIDGET_CHAT_WELCOME_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_IDENTITY_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_OFFLINE_AGENTS_EXISTING_CONVERSATION_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_OFFLINE_AGENTS_NEW_CONVERSATION_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_UNRESPONSIVE_AGENTS_SKILL_PAGE,
} from 'constants/pages'
import {
  SETTINGS_WIDGET_CREATE_CUSTOMIZE_PAGE,
  SETTINGS_WIDGET_CREATE_INSTALL_PAGE,
  SETTINGS_WIDGET_CREATE_NEW_PAGE,
} from 'subapps/settings/types'
import { selectRecentlyCreatedWidgetId } from './widgets'

export const selectWidgetsIsOnPage = createSelector(
  currentPathSelector,
  path => {
    if (!path) return false
    if (path.match(/\/settings\/integrations\/widgets/)) {
      return true
    }
    return false
  }
)

export const selectTopbarTitle = createSelector(
  selectCurrentPage,
  currentPage => {
    switch (currentPage) {
      case SETTINGS_WIDGET_CONTACT_PAGE:
        return 'Contact'
      case SETTINGS_WIDGET_KB_PAGE:
        return 'Knowledge Base'
      case SETTINGS_WIDGET_CUSTOMIZE_PAGE:
        return 'Customize'
      case SETTINGS_WIDGET_TRANSLATE_PAGE:
        return 'Language & Text'
      case SETTINGS_WIDGET_CHAT_PAGE:
      case SETTINGS_WIDGET_CHAT_CONVERSATION_PAGE:
      case SETTINGS_WIDGET_CHAT_ROUTING_PAGE:
      case SETTINGS_WIDGET_CHAT_ACL_PAGE:
      case SETTINGS_WIDGET_CHAT_BOT_OVERVIEW_PAGE:
      case SETTINGS_WIDGET_CHAT_WELCOME_SKILL_PAGE:
      case SETTINGS_WIDGET_CHAT_IDENTITY_SKILL_PAGE:
      case SETTINGS_WIDGET_CHAT_OFFLINE_AGENTS_EXISTING_CONVERSATION_SKILL_PAGE:
      case SETTINGS_WIDGET_CHAT_OFFLINE_AGENTS_NEW_CONVERSATION_SKILL_PAGE:
      case SETTINGS_WIDGET_CHAT_UNRESPONSIVE_AGENTS_SKILL_PAGE:
        return 'Live Chat'
      case SETTINGS_WIDGET_INSTALL_PAGE:
      case SETTINGS_WIDGET_FIRST_INSTALL_PAGE:
        return 'Install'
      default:
        return 'Widget'
    }
  }
)

// HACK (jscheel): We really should actually check against the list of pages,
// but matching on the name is convenient enough for now.
export const selectIsOnChatPage = createSelector(
  selectCurrentPage,
  currentPage => currentPage && currentPage.startsWith('SETTINGS_WIDGET_CHAT_')
)

export const selectIsOnCreatePage = createSelector(
  selectCurrentPage,
  currentPage =>
    currentPage && currentPage.startsWith('SETTINGS_WIDGET_CREATE_')
)

const SKILL_PAGES = [
  SETTINGS_WIDGET_CHAT_WELCOME_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_IDENTITY_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_OFFLINE_AGENTS_EXISTING_CONVERSATION_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_OFFLINE_AGENTS_NEW_CONVERSATION_SKILL_PAGE,
  SETTINGS_WIDGET_CHAT_UNRESPONSIVE_AGENTS_SKILL_PAGE,
]

export const selectIsOnChatSkillPage = createSelector(
  selectCurrentPage,
  currentPage => SKILL_PAGES.includes(currentPage)
)

const selectCreatePagesSteps = createSelector(() => {
  // As the wizard gets more complicated, we can add logic in here to return
  // different arrays of steps. Each set of steps must always end with the
  // SETTINGS_WIDGET_CREATE_INSTALL_PAGE page.
  const pages = [
    {
      page: SETTINGS_WIDGET_CREATE_NEW_PAGE,
      description:
        'Give your widget a name and choose which features you would like to enable.',
    },
    {
      page: SETTINGS_WIDGET_CREATE_CUSTOMIZE_PAGE,
      description: "Let's customize your widget.",
    },
  ]

  pages.push({
    page: SETTINGS_WIDGET_CREATE_INSTALL_PAGE,
    description: 'Install your widget on your site.',
  })

  return pages
})

const selectCreatePagesArray = createSelector(
  selectCreatePagesSteps,
  createPages => createPages.map(p => p.page)
)

export const selectPreviousCreatePageRoute = createSelector(
  selectCurrentPage,
  selectCreatePagesArray,
  (currentPage, createPages) => {
    // NOTE (jscheel): Once the wizard is finished and the widget has been created,
    // we don't let the user go back in the process. They can just edit the widget
    // in the normal settings at this point.
    if (currentPage === SETTINGS_WIDGET_CREATE_INSTALL_PAGE) return null
    const idx = createPages.indexOf(currentPage)
    const prevPage = createPages[idx - 1]
    if (prevPage) {
      return {
        type: prevPage,
      }
    }
    return null
  }
)

export const selectNextCreatePageRoute = createSelector(
  selectCurrentPage,
  selectCreatePagesArray,
  selectRecentlyCreatedWidgetId,
  (currentPage, createPages, recentWidgetId) => {
    const nextPage = createPages[createPages.indexOf(currentPage) + 1]
    if (nextPage) {
      return {
        type: nextPage,
      }
    }
    return {
      type: SETTINGS_WIDGET_CUSTOMIZE_PAGE,
      payload: {
        id: recentWidgetId,
      },
    }
  }
)

export const selectIsFirstCreateStep = createSelector(
  selectCurrentPage,
  selectCreatePagesArray,
  (currentPage, createPages) => createPages.indexOf(currentPage) === 0
)

export const selectIsFinalCreateStep = createSelector(
  selectCurrentPage,
  currentPage => currentPage === SETTINGS_WIDGET_CREATE_INSTALL_PAGE
)

// NOTE (jscheel): Naming is hard
export const selectShouldCreateWidgetFromDraftBeforeNextStep = createSelector(
  selectNextCreatePageRoute,
  nextPageRoute => nextPageRoute?.type === SETTINGS_WIDGET_CREATE_INSTALL_PAGE
)

export const selectCreateStepProgress = createSelector(
  selectCurrentPage,
  selectCreatePagesSteps,
  (currentPage, createPagesSteps) => {
    const currentStepIdx = createPagesSteps.findIndex(
      p => p.page === currentPage
    )

    return {
      step: currentStepIdx + 1,
      of: createPagesSteps.length,
      description: createPagesSteps[currentStepIdx].description,
    }
  }
)
